import React, { useRef, useEffect } from 'react';

/**
 * DataTable'ı sarar; tablonun native yatay scroll çubuğunu gizler ve
 * senkronize bir yatay scroll çubuğu gösterir.
 *
 * scrollBarPosition:
 * - 'bottom' (varsayılan): viewport altına sabitlenmiş scroll çubuğu
 * - 'top': tablonun hemen üstünde inline scroll çubuğu
 *
 * @param {React.ReactNode} children           - İçine alınan DataTable
 * @param {Array}           deps               - useEffect bağımlılıkları (tablo verisi, aktif tab vb.)
 * @param {'top'|'bottom'}  scrollBarPosition  - Scroll çubuğunun konumu
 */
const StickyScrollTable = ({ children, deps = [], scrollBarPosition = 'bottom' }) => {
    const containerRef = useRef(null);
    const scrollBarRef = useRef(null);
    const innerDivRef = useRef(null);
    const isTopScroll = scrollBarPosition === 'top';

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const scrollBar = scrollBarRef.current;
        const innerDiv = innerDivRef.current;
        if (!scrollBar || !innerDiv) return;

        let teardown = null;
        let mo = null;

        const setup = () => {
            const currentWrapper = container.querySelector('.p-datatable-wrapper');
            if (!currentWrapper) return false;

            teardown?.();

            const updateBar = () => {
                innerDiv.style.width = `${currentWrapper.scrollWidth}px`;

                if (isTopScroll) {
                    const needsScroll = currentWrapper.scrollWidth > currentWrapper.clientWidth;
                    scrollBar.style.width = `${currentWrapper.clientWidth}px`;
                    scrollBar.style.display = needsScroll ? 'block' : 'none';
                    scrollBar.scrollLeft = currentWrapper.scrollLeft;
                    return;
                }

                const rect = currentWrapper.getBoundingClientRect();
                const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
                if (isVisible) {
                    scrollBar.style.display = 'block';
                    scrollBar.style.left = `${rect.left}px`;
                    scrollBar.style.width = `${rect.width}px`;
                } else {
                    scrollBar.style.display = 'none';
                }
            };

            let syncFromWrapper = false;
            let syncFromScrollBar = false;

            const onWrapperScroll = () => {
                if (syncFromScrollBar) return;
                syncFromWrapper = true;
                scrollBar.scrollLeft = currentWrapper.scrollLeft;
                syncFromWrapper = false;
            };

            const onScrollBarScroll = () => {
                if (syncFromWrapper) return;
                syncFromScrollBar = true;
                currentWrapper.scrollLeft = scrollBar.scrollLeft;
                syncFromScrollBar = false;
            };

            updateBar();
            const rafId = requestAnimationFrame(updateBar);

            const ro = new ResizeObserver(updateBar);
            ro.observe(currentWrapper);

            const table = currentWrapper.querySelector('.p-datatable-table');
            if (table) ro.observe(table);

            currentWrapper.addEventListener('scroll', onWrapperScroll);
            scrollBar.addEventListener('scroll', onScrollBarScroll);
            window.addEventListener('resize', updateBar);

            if (!isTopScroll) {
                window.addEventListener('scroll', updateBar, true);
            }

            teardown = () => {
                cancelAnimationFrame(rafId);
                ro.disconnect();
                currentWrapper.removeEventListener('scroll', onWrapperScroll);
                scrollBar.removeEventListener('scroll', onScrollBarScroll);
                window.removeEventListener('resize', updateBar);
                if (!isTopScroll) {
                    window.removeEventListener('scroll', updateBar, true);
                }
            };

            return true;
        };

        if (!setup()) {
            mo = new MutationObserver(() => {
                if (setup()) mo.disconnect();
            });
            mo.observe(container, { childList: true, subtree: true });
        }

        return () => {
            mo?.disconnect();
            teardown?.();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTopScroll, ...deps]);

    const scrollBarStyle = {
        overflowX: 'auto',
        overflowY: 'hidden',
        height: '14px',
        maxWidth: '100%',
        ...(isTopScroll
            ? { display: 'none', marginBottom: '4px' }
            : {
                  position: 'fixed',
                  bottom: 0,
                  zIndex: 1000,
                  display: 'none'
              })
    };

    const scrollBar = (
        <div ref={scrollBarRef} style={scrollBarStyle}>
            <div ref={innerDivRef} style={{ height: '1px' }} />
        </div>
    );

    return (
        <>
            <style>{`
                .sticky-scroll-table-container .p-datatable-wrapper {
                    overflow-x: auto !important;
                    scrollbar-width: none !important;
                    -ms-overflow-style: none !important;
                }
                .sticky-scroll-table-container .p-datatable-wrapper::-webkit-scrollbar {
                    display: none !important;
                    height: 0 !important;
                }
            `}</style>

            <div ref={containerRef} className="sticky-scroll-table-container">
                {isTopScroll && scrollBar}
                {children}
            </div>

            {!isTopScroll && scrollBar}
        </>
    );
};

export default StickyScrollTable;
